type Point = { x: number; y: number };

export const useDrawingUtils = () => {
  const getCtx = (
    canvasRefs: React.MutableRefObject<
      Record<number, HTMLCanvasElement | null>
    >,
    page: number,
  ) => {
    const canvas = canvasRefs.current[page];
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return null;
    return { canvas, ctx };
  };

  const getPos = (
    e: React.PointerEvent<HTMLCanvasElement>,
    zoom: number,
  ): Point => {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom,
    };
  };

  const hexToRgba = (hex: string) => {
    const n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255, 255];
  };

  const colorMatch = (
    data: Uint8ClampedArray,
    idx: number,
    target: number[],
    tolerance: number,
  ) => {
    return (
      Math.abs(data[idx] - target[0]) <= tolerance &&
      Math.abs(data[idx + 1] - target[1]) <= tolerance &&
      Math.abs(data[idx + 2] - target[2]) <= tolerance &&
      Math.abs(data[idx + 3] - target[3]) <= tolerance
    );
  };
  const floodFill = (
    ctx: CanvasRenderingContext2D,
    startX: number,
    startY: number,
    fillHex: string,
    width: number,
    height: number,
  ) => {
    const image = ctx.getImageData(0, 0, width, height);
    const data = image.data;

    const startIdx = (startY * width + startX) * 4;
    if (startX < 0 || startY < 0 || startX >= width || startY >= height) {
      return;
    }
    const target = Array.from(data.slice(startIdx, startIdx + 4));
    const fill = hexToRgba(fillHex);

    if (
      target[0] === fill[0] &&
      target[1] === fill[1] &&
      target[2] === fill[2] &&
      target[3] === fill[3]
    ) {
      return;
    }

    const tolerance = 20;
    const stack: [number, number][] = [[startX, startY]];

    while (stack.length) {
      const [x, y] = stack.pop()!;
      let nx = x;

      while (
        nx >= 0 &&
        colorMatch(data, (y * width + nx) * 4, target, tolerance)
      ) {
        nx--;
      }
      nx++;

      let spanUp = false;
      let spanDown = false;

      while (
        nx < width &&
        colorMatch(data, (y * width + nx) * 4, target, tolerance)
      ) {
        const idx = (y * width + nx) * 4;
        data[idx] = fill[0];
        data[idx + 1] = fill[1];
        data[idx + 2] = fill[2];
        data[idx + 3] = fill[3];

        if (
          y > 0 &&
          colorMatch(data, ((y - 1) * width + nx) * 4, target, tolerance)
        ) {
          if (!spanUp) {
            stack.push([nx, y - 1]);
            spanUp = true;
          }
        } else spanUp = false;

        if (
          y < height - 1 &&
          colorMatch(data, ((y + 1) * width + nx) * 4, target, tolerance)
        ) {
          if (!spanDown) {
            stack.push([nx, y + 1]);
            spanDown = true;
          }
        } else spanDown = false;

        nx++;
      }
    }

    ctx.putImageData(image, 0, 0);
  };

  return { getCtx, getPos, hexToRgba, colorMatch, floodFill };
};
